import React from 'react';
import { Plus, Edit, Trash2, Package, ExternalLink, Settings } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

const ShopManagement = ({ ociShops, onEditShop, onDeleteShop }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative group">
        {/* Glass Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl'
          : 'glass-light-card'
        } rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : ''
        } rounded-3xl`}></div>

        <div className="relative p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative group/icon">
                <div className={isDarkMode ? 'absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm' : 'hidden'}></div>
                <div className={`relative ${isDarkMode
                  ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                  : 'bg-brand/10'
                } w-12 h-12 rounded-2xl flex items-center justify-center`}>
                  <Package className={`w-6 h-6 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                  <div className={isDarkMode ? 'absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm' : 'hidden'}></div>
                </div>
              </div>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>{t('admin.ociShops.title')}</h2>
                <p className={`${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-600'}`}>{t('admin.ociShops.subtitle')}</p>
              </div>
            </div>

            {/* Primary Button */}
            <button
              onClick={() => onEditShop({})}
              className={`relative group/button overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
                isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] text-white px-4 py-2 flex items-center font-medium shadow-sm'
              }`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(59,130,246,0.7) 0%, rgba(37,99,235,0.7) 50%, rgba(29,78,216,0.7) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : undefined}
            >
              {/* Hover Overlay - dark only */}
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/button:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                    : undefined
                }}
              ></div>

              <div className={`relative z-10 flex items-center font-medium text-white transition-all duration-300 ${isDarkMode ? 'px-4 py-2 drop-shadow-lg group-hover/button:drop-shadow-xl' : ''}`}>
                <Plus className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/button:scale-110" />
                {t('admin.ociShops.newShop')}
              </div>

              {/* Wave Effect - dark only */}
              <div className={`absolute inset-0 opacity-0 group-hover/button:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/button:translate-x-full transition-transform duration-800"
                  style={{
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                  }}
                ></div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* OCI Shops Liste */}
      {ociShops && ociShops.length > 0 ? (
        <div className="relative group">
          {/* Glass Background */}
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl'
            : 'glass-light-card'
          } rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]`}></div>
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
            : ''
          } rounded-3xl`}></div>

          {/* Glass Particles - dark only */}
          <div className={`absolute top-6 right-8 w-2 h-2 ${isDarkMode ? 'bg-white/20' : 'hidden'} rounded-full animate-pulse`}></div>

          <div className="relative p-6">
            <div className="space-y-4">
              {ociShops.map((shop) => (
                <div key={shop.id} className="relative group/item">
                  {/* Shop Card Background */}
                  <div className={`absolute inset-0 ${isDarkMode
                    ? 'bg-gradient-to-br from-gray-900/20 via-gray-800/15 to-gray-900/20 border border-white/20 backdrop-blur-xl shadow-[0_4px_16px_rgba(0,0,0,0.2)] group-hover/item:shadow-[0_8px_24px_rgba(0,0,0,0.3)]'
                    : 'bg-white border border-slate-200 shadow-sm group-hover/item:shadow-md'
                  } rounded-2xl transition-all duration-300`}></div>
                  <div className={`absolute inset-0 ${isDarkMode
                    ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10 opacity-0 group-hover/item:opacity-100'
                    : ''
                  } rounded-2xl transition-opacity duration-300`}></div>

                  <div className="relative p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {/* Shop Icon */}
                        <div className="relative group/shopicon">
                          <div className={isDarkMode ? 'absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm' : 'hidden'}></div>
                          <div className={`relative ${isDarkMode
                            ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                            : 'bg-brand/10'
                          } w-12 h-12 rounded-2xl flex items-center justify-center`}>
                            {shop.icon ? (
                              <img
                                src={shop.icon}
                                alt={`${shop.name} favicon`}
                                className="w-8 h-8 rounded object-contain relative z-10"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                            ) : null}
                            <div style={{ display: shop.icon ? 'none' : 'block' }} className="relative z-10">
                              <ExternalLink className={`w-6 h-6 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                            </div>
                            <div className={isDarkMode ? 'absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm' : 'hidden'}></div>
                          </div>
                        </div>

                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className={`font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>{shop.name}</h4>

                            {/* Status Badge */}
                            <span className={`relative inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border ${
                              shop.is_active
                                ? (isDarkMode ? 'bg-emerald-500/20 border-emerald-400/30 text-emerald-200 backdrop-blur-xl' : 'bg-emerald-50 border-emerald-200 text-emerald-700')
                                : (isDarkMode ? 'bg-red-500/20 border-red-400/30 text-red-200 backdrop-blur-xl' : 'bg-red-50 border-red-200 text-red-700')
                            }`}>
                              {shop.is_active ? t('common.active') : t('common.inactive')}
                            </span>

                            {shop.supplier && (
                              <span className={`relative inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border ${isDarkMode
                                ? 'bg-white/10 border-white/20 text-white/80 backdrop-blur-xl'
                                : 'bg-slate-100 border-slate-200 text-slate-600'
                              }`}>
                                {shop.supplier}
                              </span>
                            )}
                          </div>

                          {shop.description && (
                            <p className={`text-sm ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-600'} mb-2`}>{shop.description}</p>
                          )}

                          <div className={`flex items-center space-x-4 text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                            {shop.base_url && (
                              <span className="flex items-center">
                                <ExternalLink className="w-3 h-3 mr-1" />
                                {t('admin.ociShops.urlConfigured')}
                              </span>
                            )}
                            <span className="flex items-center">
                              <Settings className="w-3 h-3 mr-1" />
                              {shop.username ? t('admin.ociShops.credentialsConfigured') : t('admin.ociShops.credentialsNotConfigured')}
                            </span>
                            {shop.category && (
                              <span>{t('common.category')}: {shop.category}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => onEditShop(shop)}
                          className={`relative group/edit overflow-hidden rounded-xl transition-all duration-300 transform hover:scale-[1.05] active:scale-[0.95] ${
                            isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 p-2'
                          }`}
                          title={t('common.edit')}
                          style={isDarkMode ? {
                            background: 'linear-gradient(135deg, rgba(71,85,105,0.4) 0%, rgba(75,85,99,0.4) 50%, rgba(71,85,105,0.4) 100%)',
                            backdropFilter: 'blur(16px)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
                          } : undefined}
                        >
                          {/* Hover Overlay - dark only */}
                          <div
                            className={`absolute inset-0 rounded-xl transition-all duration-300 opacity-0 group-hover/edit:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                            style={{
                              background: 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.2) 50%, rgba(59,130,246,0.15) 100%)'
                            }}
                          ></div>

                          <div className={isDarkMode ? 'relative p-2' : ''}>
                            <Edit className={`w-4 h-4 transition-all duration-300 ${
                              isDarkMode ? 'text-white/70 group-hover/edit:text-blue-300' : 'text-slate-600 group-hover/edit:text-brand'
                            } group-hover/edit:scale-110`} />
                          </div>

                          {/* Wave Effect - dark only */}
                          <div className={`absolute inset-0 opacity-0 group-hover/edit:opacity-100 transition-opacity duration-400 ${isDarkMode ? '' : 'hidden'}`}>
                            <div
                              className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/edit:translate-x-full transition-transform duration-600"
                              style={{
                                background: 'linear-gradient(90deg, transparent 0%, rgba(59,130,246,0.2) 50%, transparent 100%)'
                              }}
                            ></div>
                          </div>
                        </button>
                        <button
                          onClick={() => onDeleteShop(shop.id)}
                          className={`relative group/delete overflow-hidden rounded-xl transition-all duration-300 transform hover:scale-[1.05] active:scale-[0.95] ${
                            isDarkMode ? '' : 'bg-slate-100 hover:bg-red-50 p-2'
                          }`}
                          title={t('common.delete')}
                          style={isDarkMode ? {
                            background: 'linear-gradient(135deg, rgba(71,85,105,0.4) 0%, rgba(75,85,99,0.4) 50%, rgba(71,85,105,0.4) 100%)',
                            backdropFilter: 'blur(16px)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
                          } : undefined}
                        >
                          {/* Hover Overlay - dark only */}
                          <div
                            className={`absolute inset-0 rounded-xl transition-all duration-300 opacity-0 group-hover/delete:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                            style={{
                              background: 'linear-gradient(135deg, rgba(239,68,68,0.15) 0%, rgba(220,38,38,0.2) 50%, rgba(239,68,68,0.15) 100%)'
                            }}
                          ></div>

                          <div className={isDarkMode ? 'relative p-2' : ''}>
                            <Trash2 className={`w-4 h-4 transition-all duration-300 ${
                              isDarkMode ? 'text-white/70 group-hover/delete:text-red-300' : 'text-slate-600 group-hover/delete:text-red-600'
                            } group-hover/delete:scale-110`} />
                          </div>

                          {/* Wave Effect - dark only */}
                          <div className={`absolute inset-0 opacity-0 group-hover/delete:opacity-100 transition-opacity duration-400 ${isDarkMode ? '' : 'hidden'}`}>
                            <div
                              className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/delete:translate-x-full transition-transform duration-600"
                              style={{
                                background: 'linear-gradient(90deg, transparent 0%, rgba(239,68,68,0.2) 50%, transparent 100%)'
                              }}
                            ></div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="relative group">
          {/* Glass Background */}
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl'
            : 'glass-light-card'
          } rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]`}></div>
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
            : ''
          } rounded-3xl`}></div>

          {/* Glass Particles - dark only */}
          <div className={`absolute top-6 right-8 w-2 h-2 ${isDarkMode ? 'bg-white/20' : 'hidden'} rounded-full animate-pulse`}></div>

          <div className="relative p-8">
            <div className="flex flex-col items-center justify-center py-12">
              {/* Empty State Icon */}
              <div className="relative group/empty mb-6">
                <div className={isDarkMode ? 'absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-3xl blur-sm' : 'hidden'}></div>
                <div className={`relative ${isDarkMode
                  ? 'bg-gradient-to-br from-slate-600/60 via-gray-600/50 to-slate-700/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                  : 'bg-slate-100'
                } w-16 h-16 rounded-3xl flex items-center justify-center`}>
                  <Package className={`w-8 h-8 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-slate-500'}`} />
                  <div className={isDarkMode ? 'absolute top-2 left-2 w-4 h-4 bg-white/30 rounded-full blur-sm' : 'hidden'}></div>
                </div>
              </div>

              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} mb-3`}>{t('admin.ociShops.empty')}</h3>
              <p className={`${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-600'} text-center max-w-sm mb-8`}>
                {t('admin.ociShops.emptyDesc')}
              </p>

              {/* CTA Button */}
              <button
                onClick={() => onEditShop({})}
                className={`relative group/cta overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
                  isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] text-white px-6 py-3 flex items-center font-medium shadow-sm'
                }`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(59,130,246,0.7) 0%, rgba(37,99,235,0.7) 50%, rgba(29,78,216,0.7) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                } : undefined}
              >
                {/* Hover Overlay - dark only */}
                <div
                  className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/cta:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                  }}
                ></div>

                <div className={`relative z-10 flex items-center font-medium text-white transition-all duration-300 ${isDarkMode ? 'px-6 py-3 drop-shadow-lg group-hover/cta:drop-shadow-xl' : ''}`}>
                  <Plus className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/cta:scale-110" />
                  {t('admin.ociShops.createFirst')}
                </div>

                {/* Wave Effect - dark only */}
                <div className={`absolute inset-0 opacity-0 group-hover/cta:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                  <div
                    className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/cta:translate-x-full transition-transform duration-800"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                    }}
                  ></div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopManagement;
